import React, { useMemo, useState } from "react";
import type { ColumnsType, TableProps } from "antd/es/table";
import { App, Button, Table, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { FileTextOutlined } from "@ant-design/icons";

const { Text } = Typography;

export type RowItem = {
  id: string;
  slideNumber: number;
  contentType: string;
  fileName: string;
  fileLocation: string;
  lastUpdated: string;
  placeholder: any;
};

export type ContentManagerTableProps = {
  data?: RowItem[];
  loading?: boolean;
  onEdit?: (row: RowItem) => void;
  onOpen?: (row: RowItem) => void;
  onRefresh?: (row: RowItem) => Promise<void> | void;
  onResize?: (row: RowItem) => void;
  pageSize?: number;
  rowSelectionEnabled?: boolean;
};

export const ContentManagerTable: React.FC<ContentManagerTableProps> = ({
  data,
  loading,
  onEdit,
  onOpen,
  onRefresh,
  onResize,
  pageSize = 15,
  rowSelectionEnabled = false,
}) => {
  const { message } = App.useApp();
  const baseRows = data && data.length > 0 ? data : [];
  type RowWithKey = RowItem & { _rk: string };
  const rows: RowWithKey[] = baseRows.map((r, idx) => ({ ...r, _rk: `${r.id}-${idx}` }));
  const [refreshingRowId, setRefreshingRowId] = useState<string | null>(null);

  const contentTypeFilters = useMemo(() => {
    const unique = Array.from(new Set(rows.map((r) => r.contentType))).filter(Boolean);
    return unique.map((u) => ({ text: u, value: u }));
  }, [rows]);

  const solidHeaderStyle = { background: "#ffffff", border: "none", borderBottom: "2px solid #000", verticalAlign: "bottom" } as const;
  const dashedHeaderStyle = { background: "#ffffff", border: "none", borderBottom: "2px solid #000" } as const;
  const solidHeader = { onHeaderCell: () => ({ style: solidHeaderStyle }) } as const;
  const dashedHeader = { onHeaderCell: () => ({ style: dashedHeaderStyle }) } as const;
  const noRowBorder = { onCell: () => ({ style: { border: "none", borderBottom: "none", borderRight: "none", borderLeft: "none" } }) } as const;
  const paddingRight = { style: { paddingRight: 10 } } as const;

  const columns: ColumnsType<RowWithKey> = [
    {
      title: "Slide Number",
      dataIndex: "slideNumber",
      key: "slideNumber",
      width: 50,
      sorter: (a, b) => a.slideNumber - b.slideNumber,
      defaultSortOrder: undefined,
      ...solidHeader,
      ...noRowBorder,
    },
    {
      title: "Content Type",
      dataIndex: "contentType",
      key: "contentType",
      width: 160,
      filters: contentTypeFilters,
      render: (text: string) => <Text>{text}</Text>,
      ...solidHeader,
      ...noRowBorder,
    },
    {
      title: "Excel File",
      key: "excelFile",
      align: "left",
      children: [
        {
          title: "Name",
          dataIndex: "fileName",
          key: "fileName",
          width: 120,
          render: (text: string) => (
            <Tooltip title={text} placement="topLeft">
              <Text style={{ maxWidth: 180 }} ellipsis>
                {text}
              </Text>
            </Tooltip>
          ),
          ...solidHeader,
          ...noRowBorder,
          ...paddingRight,
        },
        {
          title: "Location",
          dataIndex: "fileLocation",
          key: "fileLocation",
          width: 160,
          render: (text: string) => (
            <Tooltip title={text} placement="topLeft">
              <Text style={{ maxWidth: 260 }} ellipsis>
                {text}
              </Text>
            </Tooltip>
          ),
          ...solidHeader,
          ...noRowBorder,
        },
      ],
      ...dashedHeader,
      ...noRowBorder,
    },
    {
      title: "Last Updated",
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      width: 80,
      align: "center",
      sorter: (a, b) => dayjs(a.lastUpdated).valueOf() - dayjs(b.lastUpdated).valueOf(),
      render: (iso: string) => <Text>{dayjs(iso).format("MM/DD/YYYY")}</Text>,
      ...solidHeader,
      ...noRowBorder,
    },
    {
      title: "Alpha Edit",
      key: "alphaEdit",
      width: 60,
      render: (_: unknown, row: RowWithKey) => (
        <div>
          <Button
            type="default"
            size="small"
            block
            style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center"}}
            onClick={() => (onEdit ? onEdit(row) : message.warning("Edit action isn't wired"))}
            aria-label={`Edit document ${row.fileName}`}
            data-testid="edit-btn"
          >
            <img src="assets/addin_icons/Alpha_Edit.png" alt="Alpha Edit" style={{ width: 16, height: 16 }} />
          </Button>
        </div>
      ),
      ...solidHeader,
      ...noRowBorder,
    },
    {
      title: "Open Excel",
      key: "openExcel",
      width: 60,
      render: (_: unknown, row: RowWithKey) => (
        <div>
          <Button
            type="default"
            size="small"
            block
            style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center"}}
            onClick={() => (onOpen ? onOpen(row) : message.warning("Open action isn't wired"))}
            aria-label={`Open Excel for ${row.fileName}`}
            data-testid="open-btn"
            disabled={true}
          >
            <img src="assets/addin_icons/Settings.png" alt="Open Excel" style={{ width: 16, height: 16 }} />
          </Button>
        </div>
      ),
      ...solidHeader,
      ...noRowBorder,
    },
    {
      title: "Refresh",
      key: "refresh",
      width: 50,
      render: (_: unknown, row: RowWithKey) => (
        <div>
          <Button
            type="default"
            size="small"
            block
            style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center"}}
            loading={refreshingRowId === row.id}
            onClick={async () => {
              if (!onRefresh) {
                message.warning("Refresh action isn't wired");
                return;
              }
              try {
                setRefreshingRowId(row.id);
                await onRefresh(row);
              } finally {
                setRefreshingRowId(null);
              }
            }}
            aria-label={`Refresh data for ${row.fileName}`}
            data-testid="refresh-btn"
          >
            <img src="assets/addin_icons/Refresh.png" alt="Refresh" style={{ width: 16, height: 16 }} />
          </Button>
        </div>
      ),
      ...solidHeader,
      ...noRowBorder,
    },
    {
      title: "Resize",
      key: "resize",
      width: 50,
      render: (_: unknown, row: RowWithKey) => (
        <div>
          <Button
            type="default"
            size="small"
            block
            style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center"}}
            onClick={() => (onResize ? onResize(row) : message.warning("Resize action isn't wired"))}
            aria-label={`Resize for slide ${row.slideNumber}`}
            data-testid="resize-btn"
          >
            <img src="assets/addin_icons/ResizeIcon.png" alt="Resize" style={{ width: 16, height: 16 }} />
          </Button>
        </div>
      ),
      ...solidHeader,
      ...noRowBorder,
    },
  ];

  const tableProps: TableProps<RowWithKey> = {
    rowKey: (r) => r._rk,
    columns,
    dataSource: rows,
    size: "small",
    bordered: false,
    sticky: { offsetHeader: 0 },
    loading,
    pagination: { pageSize, showSizeChanger: false },
    scroll: { x: 1200 },
    rowClassName: "bg-white",
    style: { border: "none" },
    locale: {
      emptyText: (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <FileTextOutlined style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }} />
          <div style={{ color: "#8c8c8c", fontSize: 14 }}>
            No AlphaScript content found in your presentation.
          </div>
        </div>
      ),
    },
  };

  return (
    <div style={{ position: "relative" }}>
      <style>
        {`
          .ant-table-tbody > tr > td::before,
          .ant-table-tbody > tr > td::after {
            display: none !important;
          }
          .ant-table-thead > tr > th::before,
          .ant-table-thead > tr > th::after {
            display: none !important;
          }
        `}
      </style>
      <Table
        {...tableProps}
        rowSelection={rowSelectionEnabled ? { type: "checkbox" } : undefined}
      />
    </div>
  );
};

export default ContentManagerTable;


